import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { FirebaseGuard } from './components/FirebaseGuard';
import React, { useEffect } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Instructor from './components/Instructor';
import BlogPosts from './components/BlogPosts';
import FAQ from './components/FAQ';
import Footer from './components/Footer';
const BlogPage = React.lazy(() => import('./pages/BlogPage'));
const BlogPostDetail = React.lazy(() => import('./pages/BlogPostDetail'));
const PaymentSuccess = React.lazy(() => import('./components/PaymentSuccess'));
const Courses = React.lazy(() => import('./components/Courses'));
const Schedule = React.lazy(() => import('./components/Schedule'));
const Contact = React.lazy(() => import('./components/Contact'));
// Lazy load admin components to prevent Firebase initialization on public pages
const AdminLogin = React.lazy(() => import('./components/admin/AdminLogin'));
const AdminLoginDebug = React.lazy(() => import('./components/admin/AdminLoginDebug'));
const SessionManager = React.lazy(() => import('./components/admin/SessionManager'));
const DefaultsManager = React.lazy(() => import('./components/admin/DefaultsManager'));
const ParticipantManager = React.lazy(() => import('./components/admin/ParticipantManager'));
const ConsentAuditDashboard = React.lazy(() => import('./components/admin/ConsentAuditDashboard'));
const TestimonialsManager = React.lazy(() => import('./components/admin/TestimonialsManager'));
const ProtectedRoute = React.lazy(() => import('./components/admin/ProtectedRoute'));
const AdminPageWrapper = React.lazy(() => import('./components/admin/AdminPageWrapper'));
const ZohoCallback = React.lazy(() => import('./components/admin/ZohoCallback').then(module => ({ default: module.ZohoCallback })));
const AdminManagement = React.lazy(() => import('./components/admin/AdminManagement'));
const DashboardContainer = React.lazy(() => import('./components/admin/DashboardContainer'));
const UnifiedAnalyticsDashboard = React.lazy(() => import('./components/admin/UnifiedAnalyticsDashboard').then(module => ({ default: module.UnifiedAnalyticsDashboard })));
const AnalyticsRedirect = React.lazy(() => import('./components/admin/AnalyticsRedirect').then(module => ({ default: module.AnalyticsRedirect })));
const BlogManagement = React.lazy(() => import('./components/admin/BlogManagement'));
const CourseManagement = React.lazy(() => import('./components/admin/CourseManagement'));
const ManualParticipantForm = React.lazy(() => import('./components/admin/ManualParticipantForm'));
const AuthenticationDiagnosticDashboard = React.lazy(() => import('./components/admin/AuthenticationDiagnosticDashboard'));

import { Toaster } from './components/ui/toaster';
const PaymentResult = React.lazy(() => import('./components/PaymentResult'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));
const CookiesPage = React.lazy(() => import('./pages/CookiesPage'));
const UnsubscribePage = React.lazy(() => import('./pages/UnsubscribePage'));
const TwitterEmbedTest = React.lazy(() => import('./pages/TwitterEmbedTest'));
// Cookie consent provider removed
import { CookieConsent } from './components/ui/cookie-consent/CookieConsent';
import { AnalyticsProvider } from './lib/AnalyticsProvider';
import AnalyticsTest from './components/AnalyticsTest';
const AdminAnalyticsTest = React.lazy(() => import('./components/admin/AnalyticsTest'));
const SocialMediaDemo = React.lazy(() => import('./pages/SocialMediaDemo'));
const LinkedInShareDemo = React.lazy(() => import('./pages/LinkedInShareDemo'));
const FacebookSharingDebug = React.lazy(() => import('./pages/FacebookSharingDebug'));
import ErrorBoundary from './components/ErrorBoundary';

function App() {
  return (
    <React.Fragment>
      <Router>
        <AnalyticsProvider>
          <FirebaseGuard />
          <div className="min-h-screen bg-background dark:bg-gray-900 text-foreground transition-colors duration-200">
          <Routes>
            <Route path="/auth/zoho/callback" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <ZohoCallback />
              </React.Suspense>
            } />
            <Route path="/admin" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <AdminLogin />
              </React.Suspense>
            } />
            <Route path="/admin/debug" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <AdminLoginDebug />
              </React.Suspense>
            } />
            <Route path="/admin/sessions" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <ProtectedRoute>
                  <AdminPageWrapper title="Sessions & Workshops">
                    <SessionManager />
                  </AdminPageWrapper>
                </ProtectedRoute>
              </React.Suspense>
            } />
            <Route path="/admin/defaults" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <ProtectedRoute>
                  <AdminPageWrapper title="Course Defaults">
                    <DefaultsManager />
                  </AdminPageWrapper>
                </ProtectedRoute>
              </React.Suspense>
            } />
            <Route path="/admin/participants" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <ProtectedRoute>
                  <AdminPageWrapper title="Participants">
                    <ParticipantManager />
                  </AdminPageWrapper>
                </ProtectedRoute>
              </React.Suspense>
            } />
            <Route path="/admin/participants/add" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <ProtectedRoute>
                  <AdminPageWrapper title="Add Manual Participant">
                    <ManualParticipantForm />
                  </AdminPageWrapper>
                </ProtectedRoute>
              </React.Suspense>
            } />
            <Route path="/admin/consent-audit" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <ProtectedRoute>
                  <AdminPageWrapper title="Consent Audit">
                    <ConsentAuditDashboard />
                  </AdminPageWrapper>
                </ProtectedRoute>
              </React.Suspense>
            } />
            <Route path="/admin/testimonials" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <ProtectedRoute>
                  <AdminPageWrapper title="Testimonials">
                    <TestimonialsManager />
                  </AdminPageWrapper>
                </ProtectedRoute>
              </React.Suspense>
            } />
            <Route path="/admin/manage" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <ProtectedRoute>
                  <AdminPageWrapper title="Admin Users">
                    <AdminManagement />
                  </AdminPageWrapper>
                </ProtectedRoute>
              </React.Suspense>
            } />
            {/* Main dashboard route */}
            <Route path="/admin/dashboard" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <ProtectedRoute>
                  <AdminPageWrapper title="Dashboard">
                    <DashboardContainer />
                  </AdminPageWrapper>
                </ProtectedRoute>
              </React.Suspense>
            } />
            {/* Analytics routes removed */}
            {/* Add placeholder routes for new navigation items */}
            <Route path="/admin/courses" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <ProtectedRoute>
                  <AdminPageWrapper title="Course Management">
                    <CourseManagement />
                  </AdminPageWrapper>
                </ProtectedRoute>
              </React.Suspense>
            } />
            <Route path="/admin/content" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <ProtectedRoute>
                  <AdminPageWrapper title="Content Management">
                    <div className="text-center py-8">
                      <h2 className="text-lg font-semibold mb-2">Content Management</h2>
                      <p className="text-muted-foreground">Use the sidebar to navigate to specific content management sections.</p>
                    </div>
                  </AdminPageWrapper>
                </ProtectedRoute>
              </React.Suspense>
            } />
            <Route path="/admin/blog" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <ProtectedRoute>
                  <AdminPageWrapper title="Blog Management">
                    <BlogManagement />
                  </AdminPageWrapper>
                </ProtectedRoute>
              </React.Suspense>
            } />
            <Route path="/admin/settings" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <ProtectedRoute>
                  <AdminPageWrapper title="Settings">
                    <div className="text-center py-8">
                      <h2 className="text-lg font-semibold mb-2">Settings</h2>
                      <p className="text-muted-foreground">Use the sidebar to navigate to specific settings sections.</p>
                    </div>
                  </AdminPageWrapper>
                </ProtectedRoute>
              </React.Suspense>
            } />
            {/* Unified Analytics Dashboard */}
            <Route path="/admin/analytics" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <ProtectedRoute>
                  <AdminPageWrapper title="Analytics Dashboard">
                    <UnifiedAnalyticsDashboard />
                  </AdminPageWrapper>
                </ProtectedRoute>
              </React.Suspense>
            } />
            {/* Authentication Diagnostic Dashboard */}
            <Route path="/admin/auth-diagnostics" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <ProtectedRoute>
                  <AdminPageWrapper title="Authentication Diagnostics">
                    <AuthenticationDiagnosticDashboard />
                  </AdminPageWrapper>
                </ProtectedRoute>
              </React.Suspense>
            } />
            
            {/* Redirect old analytics routes to unified dashboard */}
            <Route path="/admin/blog-analytics" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <ProtectedRoute>
                  <AnalyticsRedirect />
                </ProtectedRoute>
              </React.Suspense>
            } />
            <Route path="/admin/engagement" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <ProtectedRoute>
                  <AnalyticsRedirect />
                </ProtectedRoute>
              </React.Suspense>
            } />
            <Route path="/admin/website-analytics" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <ProtectedRoute>
                  <AnalyticsRedirect />
                </ProtectedRoute>
              </React.Suspense>
            } />
            <Route path="/admin/analytics-test" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <ProtectedRoute>
                  <AdminPageWrapper title="Analytics Test">
                    <AdminAnalyticsTest />
                  </AdminPageWrapper>
                </ProtectedRoute>
              </React.Suspense>
            } />
            <Route path="/admin/social-media-demo" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <ProtectedRoute>
                  <AdminPageWrapper title="Social Media Tracking Demo">
                    <SocialMediaDemo />
                  </AdminPageWrapper>
                </ProtectedRoute>
              </React.Suspense>
            } />

            <Route path="/vop" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <TermsPage />
              </React.Suspense>
            } />
            <Route path="/terms" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <TermsPage />
              </React.Suspense>
            } />
            <Route path="/privacy" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <PrivacyPage />
              </React.Suspense>
            } />
            <Route path="/cookies" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <CookiesPage />
              </React.Suspense>
            } />
            <Route path="/unsubscribe" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <UnsubscribePage />
              </React.Suspense>
            } />
            <Route path="/linkedin-demo" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <Navbar />
                <LinkedInShareDemo />
                <Footer />
              </React.Suspense>
            } />
            <Route path="/facebook-debug" element={<FacebookSharingDebug />} />
            <Route path="/twitter-embed-test" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <Navbar />
                <TwitterEmbedTest />
                <Footer />
              </React.Suspense>
            } />
            <Route path="/blog" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <Navbar />
                <BlogPage />
                <Footer />
              </React.Suspense>
            } />
            <Route path="/blog/:slug" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <Navbar />
                <BlogPostDetail />
                <Footer />
              </React.Suspense>
            } />
            <Route path="/" element={
              <>
                <Navbar />
                <main>
                  <Hero />
                  <ErrorBoundary fallback={<div className="container mx-auto px-4 py-10 text-center text-sm text-muted-foreground">Kurzy sa nepodarilo načítať. Skúste obnoviť stránku.</div>}>
                    <React.Suspense fallback={<div className="container mx-auto px-4 py-10 text-center text-sm text-muted-foreground">Načítavam kurzy…</div>}>
                      <Courses />
                    </React.Suspense>
                  </ErrorBoundary>
                  <ErrorBoundary fallback={<div className="container mx-auto px-4 py-10 text-center text-sm text-muted-foreground">Termíny sa nepodarilo načítať. Skúste obnoviť stránku.</div>}>
                    <React.Suspense fallback={<div className="container mx-auto px-4 py-10 text-center text-sm text-muted-foreground">Načítavam termíny…</div>}>
                      <Schedule />
                    </React.Suspense>
                  </ErrorBoundary>
                  <Instructor />
                  <BlogPosts />
                  <FAQ />
                  <ErrorBoundary fallback={<div className="container mx-auto px-4 py-10 text-center text-sm text-muted-foreground">Kontakt sa nepodarilo načítať. Skúste obnoviť stránku.</div>}>
                    <React.Suspense fallback={<div className="container mx-auto px-4 py-10 text-center text-sm text-muted-foreground">Načítavam kontakt…</div>}>
                      <Contact />
                    </React.Suspense>
                  </ErrorBoundary>
                  <Footer />
                </main>
              </>
            } />
            <Route path="/success" element={<PaymentSuccess />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-cancelled" element={<PaymentResult />} />
          </Routes>
            <CookieConsent />
            <Toaster />
          </div>
        </AnalyticsProvider>
      </Router>
    </React.Fragment>
  );
}

export default App;
